import { type ApolloError, makeVar, useReactiveVar } from '@apollo/client';
import deepEqual from 'react-fast-compare';

import type { Experience } from '../../../types/CoreShoppingExperience';

interface ExperienceState {
  experience: Experience | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

const initialState = {
  experience: undefined,
  loading: true,
  error: undefined,
};

const experience = makeVar<ExperienceState>(initialState);

/**
 * Should be used directly only for tests purposes. Use the hook otherwise
 */
export const setExperience = (experienceValue: Experience | undefined, more: Partial<ExperienceState> = {}) => {
  const current = experience();
  if (!deepEqual(current, experienceValue)) {
    experience({
      ...current,
      experience: experienceValue,
      ...more,
    });
  }
};

const useExperience = <T = Experience>() => {
  const reactiveExperience = useReactiveVar(experience);

  return {
    actions: {
      setExperience,
      resetExperience: () => experience(initialState),
      setLoading: (loading: boolean) => experience({ ...experience(), loading }),
      setError: (error: ApolloError | undefined) => experience({ ...experience(), error }),
    },
    states: {
      experience: reactiveExperience.experience as T,
      loading: reactiveExperience.loading,
      error: reactiveExperience.error,
    },
  };
};

export default useExperience;
